import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizardStatus, useWizardSubmit, useLogout, useUser } from '@/hooks';
import { Step1ProductService } from '@/components/wizard/Step1ProductService';
import { Step2Company } from '@/components/wizard/Step2Company';
import { Step2Competitors } from '@/components/wizard/Step2Competitors';
import { StepSocialConnections } from '@/components/wizard/StepSocialConnections';
import { Step4ContentTypes } from '@/components/wizard/Step4ContentTypes';
import { Step5SalesTargets } from '@/components/wizard/Step5SalesTargets';
import { Step6PitchStrategy } from '@/components/wizard/Step6PitchStrategy';
import { Step7Calendly } from '@/components/wizard/Step7Calendly';
import { Step8Payment } from '@/components/wizard/Step8Payment';
import { Spinner } from '@/components/ui';

const Wizard: FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { status, loading: statusLoading, silentRefetch } = useWizardStatus();
  const { submitStep, completeWizard, submitting } = useWizardSubmit();
  const { logout } = useLogout();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 9;
  const hasInitialized = useRef(false);

  // Only external users should have access to wizard
  // If an internal user somehow reaches here, redirect to dashboard
  useEffect(() => {
    if (user && user.role === 'internal') {
      console.warn('Internal user attempted to access wizard. Redirecting to dashboard.');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Set initial step from saved wizard status (only once on mount)
  useEffect(() => {
    if (!status || hasInitialized.current) return; // Wait for status to load, and only run once

    if (!status.wizardCompleted) {
      // Resume from next step after last saved step, but cap at totalSteps
      const nextStep = Math.min(status.wizardStep + 1, totalSteps);
      setCurrentStep(nextStep);
      hasInitialized.current = true;
    } else {
      // If wizard is already completed, redirect to dashboard
      navigate('/dashboard', { replace: true });
      hasInitialized.current = true;
    }
  }, [status, navigate]);

  const handleNext = async (stepNum: number, data: any) => {
    const result = await submitStep(stepNum, data);
    if (result.success) {
      // Navigate to next step immediately for smooth transition
      if (stepNum < totalSteps) {
        setCurrentStep(stepNum + 1);
      }
      // Silently refetch status in the background to get the latest saved data
      // This updates status without showing loading state or causing flickering
      // Fire and forget - don't await to avoid blocking navigation
      silentRefetch().catch(console.error);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Navigate back smoothly without refetching
      // Data is already available from initial load
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    const result = await completeWizard();
    if (result.success) {
      navigate('/dashboard');
    }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  const stepTitles = [
    'Product/Service',
    'Company Profile',
    'Competitors',
    'Social Connections',
    'Content Generation',
    'Sales Target',
    'Pitch Strategy',
    'Calendly',
    'Choose Plan'
  ];

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sidebar - Step Navigation */}
      <div className="w-80 bg-slate-50 border-r border-slate-200 p-8">
        <div className="mb-8">
          <h1 className="text-lg font-semibold text-slate-900">Company Setup</h1>
          <p className="text-sm text-slate-500 mt-1">
            Complete your profile to get started
          </p>
        </div>

        {/* Step List */}
        <div className="space-y-1">
          {stepTitles.map((title, index) => {
            const stepNum = index + 1;
            const isCompleted = stepNum < currentStep;
            const isCurrent = stepNum === currentStep;

            return (
              <div
                key={stepNum}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isCurrent ? 'bg-white shadow-sm' : ''
                  }`}
              >
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${isCompleted
                    ? 'bg-emerald-500 text-white'
                    : isCurrent
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-200 text-slate-400'
                    }`}
                >
                  {isCompleted ? (
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    stepNum
                  )}
                </div>
                <span
                  className={`text-sm font-medium ${isCurrent ? 'text-slate-900' : 'text-slate-500'
                    }`}
                >
                  {title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress Footer */}
        <div className="mt-8 pt-8 border-t border-slate-200">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-slate-500">Progress</span>
            <span className="font-medium text-slate-900">
              {currentStep}/{totalSteps} steps
            </span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-slate-900 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={logout}
          className="w-full mt-6 px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-12">
        <div className="max-w-2xl">
          {submitting && (
            <div className="mb-6 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-2">
              <Spinner size="sm" />
              <span className="text-sm text-slate-600">Saving...</span>
            </div>
          )}

          <div className="transition-opacity duration-200">
            {currentStep === 1 && (
              <Step1ProductService
                key="step-1"
                initialData={status?.data}
                onNext={(data) => handleNext(1, data)}
              />
            )}

            {currentStep === 2 && (
              <Step2Company
                key="step-2"
                initialData={status?.data ? {
                  ...status.data,
                  keyPeople: status.data.keyPeople?.map(person => ({
                    ...person,
                    familyName: person.familyName || '',
                  }))
                } : undefined}
                onNext={(data) => handleNext(2, data)}
                onBack={handleBack}
              />
            )}

            {currentStep === 3 && (
              <Step2Competitors
                key="step-3"
                initialData={status?.data}
                onNext={(data) => handleNext(3, data)}
                onBack={handleBack}
              />
            )}

            {currentStep === 4 && (
              <StepSocialConnections
                key="step-4"
                initialData={status?.data}
                onNext={(data) => handleNext(4, data)}
                onBack={handleBack}
              />
            )}

            {currentStep === 5 && (
              <Step4ContentTypes
                key="step-5"
                initialData={status?.data}
                onNext={(data) => handleNext(5, data)}
                onBack={handleBack}
              />
            )}

            {currentStep === 6 && (
              <Step5SalesTargets
                key="step-6"
                initialData={status?.data?.salesTarget ? { salesTarget: status.data.salesTarget } : undefined}
                onNext={(data) => handleNext(6, data)}
                onBack={handleBack}
              />
            )}

            {currentStep === 7 && (
              <Step6PitchStrategy
                key="step-7"
                initialData={status?.data}
                onNext={(data) => handleNext(7, data)}
                onBack={handleBack}
              />
            )}

            {currentStep === 8 && (
              <Step7Calendly
                key="step-8"
                initialData={status?.data}
                onNext={(data) => handleNext(8, data)}
                onBack={handleBack}
              />
            )}

            {currentStep === 9 && (
              <Step8Payment
                key="step-9"
                onBack={handleBack}
                onComplete={handleComplete}
                hasPendingPayment={!!(status && status.wizardStep >= 9 && !status.wizardCompleted)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wizard;

